import m from 'mithril';
import { ApiService } from './services/api';

export interface Product {
    id: number;
    name: string;
    slug: string;
    description: string;
    price: string;
    stock: number;
    category: string | null;
    is_active: boolean;
    images: string[];
}

export interface User {
    id: number;
    email: string;
    name: string;
    role: 'admin' | 'customer';
}

export interface CartItem {
    id: number;
    quantity: number;
    product: {
        id: number;
        name: string;
        price: string;
    };
}

export interface State {
    products: Product[];
    cart: CartItem[];
    user: User | null;
    isAuthModalOpen: boolean;
    isSideMenuOpen: boolean;
    sidebarCollapsed: boolean;
    activeCategory: string;
    sortBy: 'default' | 'price-asc' | 'price-desc' | 'name';
    searchQuery: string;
    loading: boolean;
    error: string | null;
    theme: string;
    toasts: Toast[];
    isCartOpen: boolean;
    favorites: Product[];
    promotions: any[];
    categories: any[];
}

export interface Toast {
    id: number;
    message: string;
    type: 'success' | 'error' | 'info';
}

export const Store = {
    state: {
        products: [],
        cart: [],
        user: null,
        categories: JSON.parse(localStorage.getItem('cached_categories') || '[]'),
        isAuthModalOpen: false,
        isSideMenuOpen: false,
        sidebarCollapsed: false,
        activeCategory: 'all',
        sortBy: 'default',
        searchQuery: '',
        loading: false,
        error: null,
        theme: 'theme-day',
        toasts: [],
        isCartOpen: false,
        favorites: [],
        promotions: []
    } as State,

    setTheme(newTheme: State['theme']) {
        this.state.theme = newTheme;
        document.body.className = newTheme; // Apply to body for global variables
    },

    addToast(message: string, type: Toast['type'] = 'info') {
        const id = Date.now();
        this.state.toasts.push({ id, message, type });
        m.redraw();
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
            this.state.toasts = this.state.toasts.filter(t => t.id !== id);
            m.redraw();
        }, 3000);
    },

    async toggleFavorite(product: Product) {
        if (!this.state.user) {
            this.addToast('Please login to save favorites', 'error');
            this.state.isAuthModalOpen = true;
            m.redraw();
            return;
        }
        
        const isFav = this.state.favorites.some(f => f.id === product.id);
        
        // Optimistic UI Update
        if (isFav) {
            this.state.favorites = this.state.favorites.filter(f => f.id !== product.id);
            this.addToast('Removed from favorites', 'info');
        } else {
            this.state.favorites.push(product);
            this.addToast('Added to favorites', 'success');
        }
        m.redraw();

        try {
            if (isFav) {
                await ApiService.removeFavorite(product.id);
            } else {
                await ApiService.addFavorite(product.id);
            }
        } catch (e) {
            // Revert on failure
            this.addToast('Error updating favorites', 'error');
            if (isFav) {
                this.state.favorites.push(product);
            } else {
                this.state.favorites = this.state.favorites.filter(f => f.id !== product.id);
            }
            m.redraw();
        }
    },

    _searchTimeout: null as any,
    handleSearch(query: string) {
        this.state.searchQuery = query;
        this.state.loading = true;
        m.redraw();
        
        if (this._searchTimeout) clearTimeout(this._searchTimeout);
        this._searchTimeout = setTimeout(async () => {
            const result = await ApiService.getProducts(this.state.searchQuery);
            if (result.success) {
                this.state.products = result.data;
            } else {
                this.state.error = 'Search failed';
            }
            this.state.loading = false;
            m.redraw();
        }, 300); // 300ms debounce
    },

    async initApp() {
        this.state.loading = true;
        this.state.error = null;
        
        try {
            // First, attempt to restore user session gracefully
            try {
                const profileResult = await ApiService.getProfile();
                if (profileResult.success && profileResult.user) {
                    this.state.user = profileResult.user;
                    
                    // User is logged in, pull their favorites and cart
                    try {
                        const [favsRes, cartRes] = await Promise.all([
                            ApiService.getFavorites(),
                            ApiService.getCart()
                        ]);
                        if (favsRes?.success) {
                            this.state.favorites = favsRes.data.map((f: any) => f.product);
                        }
                        if (cartRes?.success) {
                            this.state.cart = cartRes.data;
                        }
                    } catch (e: any) {
                        if (e.message?.includes('401') || e.message?.includes('Unauthorized') || e.message?.includes('login')) {
                            // Token is invalid/expired
                            this.state.user = null;
                            ApiService.logout();
                        }
                    }
                }
            } catch (e) {
                // Ignore profile fetch failure (user just not logged in)
            }

            // Load products, promotions, and categories in parallel
            const [prodRes, promoRes, catRes] = await Promise.all([
                ApiService.getProducts(),
                ApiService.getActivePromotions(),
                ApiService.getCategories()
            ]);
            
            if (prodRes.success) {
                this.state.products = prodRes.data;
            } else {
                this.state.error = 'Failed to load products format';
            }

            if (catRes?.success) {
                this.state.categories = catRes.data;
                try {
                    localStorage.setItem('cached_categories', JSON.stringify(catRes.data));
                } catch (e) {}
            }
            
            if (promoRes?.success) {
                this.state.promotions = promoRes.data;
            }
        } catch (err: any) {
             this.state.error = err.message || 'Error occurred while loading app';
        } finally {
             this.state.loading = false;
             m.redraw();
        }
    }
}
